import React, { useEffect, useMemo, useState } from "react";
import { PageHero } from "@/components/Section";
import { SEO } from "@/components/SEO";
import api from "@/lib/api";
import { Calendar, MapPin, Loader2, ChevronLeft, ChevronRight, List, CalendarDays } from "lucide-react";

const fmt = (iso) => {
  try {
    const d = new Date(iso);
    return {
      day: d.toLocaleDateString("fr-FR", { day: "2-digit" }),
      month: d.toLocaleDateString("fr-FR", { month: "short" }),
      full: d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
      time: d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
    };
  } catch {
    return { day: "", month: "", full: "", time: "" };
  }
};

const dayKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export default function Agenda() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("calendar");

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="agenda-page">
      <SEO title="Agenda" description="Retrouvez tous les événements, rassemblements et rendez-vous du mouvement DEFAR - Les Bâtisseurs." />
      <PageHero
        overline="Agenda"
        title="Nos prochains événements"
        subtitle="Meetings, forums citoyens et rencontres : retrouvez l'agenda du mouvement et participez à nos rassemblements."
      />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* View toggle */}
        <div className="flex justify-end mb-8">
          <div className="inline-flex border border-border rounded-sm overflow-hidden">
            <button
              data-testid="view-calendar"
              onClick={() => setView("calendar")}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors ${view === "calendar" ? "bg-brand-blue text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            >
              <CalendarDays size={16} /> Calendrier
            </button>
            <button
              data-testid="view-list"
              onClick={() => setView("list")}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors ${view === "list" ? "bg-brand-blue text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            >
              <List size={16} /> Liste
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-brand-blue" size={32} /></div>
        ) : events.length === 0 ? (
          <p className="text-center text-slate-500 py-20">Aucun événement programmé pour le moment.</p>
        ) : view === "calendar" ? (
          <CalendarView events={events} />
        ) : (
          <EventList events={events} />
        )}
      </section>
    </div>
  );
}

function CalendarView({ events }) {
  const eventsByDay = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const k = dayKey(new Date(e.start_date));
      (map[k] = map[k] || []).push(e);
    });
    return map;
  }, [events]);

  const firstEventDate = useMemo(() => {
    if (!events.length) return new Date();
    const sorted = [...events].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    return new Date(sorted[0].start_date);
  }, [events]);

  const [cursor, setCursor] = useState(new Date(firstEventDate.getFullYear(), firstEventDate.getMonth(), 1));
  const [selected, setSelected] = useState(dayKey(firstEventDate));

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayKey = dayKey(new Date());

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const move = (delta) => setCursor(new Date(year, month + delta, 1));

  const selectedEvents = eventsByDay[selected] || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-7">
        {/* Month nav */}
        <div className="flex items-center justify-between mb-4">
          <button data-testid="cal-prev" onClick={() => move(-1)} className="h-9 w-9 flex items-center justify-center border border-border hover:bg-slate-50 transition-colors">
            <ChevronLeft size={18} />
          </button>
          <h3 data-testid="cal-month-label" className="text-lg font-bold text-slate-900">{MONTHS[month]} {year}</h3>
          <button data-testid="cal-next" onClick={() => move(1)} className="h-9 w-9 flex items-center justify-center border border-border hover:bg-slate-50 transition-colors">
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {WEEKDAYS.map((w) => (
            <div key={w} className="text-center text-xs font-bold text-slate-400 uppercase py-2">{w}</div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 gap-1">
          {cells.map((date, i) => {
            if (!date) return <div key={`e${i}`} className="aspect-square" />;
            const k = dayKey(date);
            const dayEvents = eventsByDay[k] || [];
            const hasEvents = dayEvents.length > 0;
            const isSelected = k === selected;
            const isToday = k === todayKey;
            return (
              <button
                key={k}
                data-testid={hasEvents ? `cal-day-${k}` : undefined}
                onClick={() => hasEvents && setSelected(k)}
                disabled={!hasEvents}
                className={`aspect-square flex flex-col items-center justify-center rounded-sm border text-sm transition-colors relative
                  ${isSelected && hasEvents ? "bg-brand-green text-white border-brand-green" : ""}
                  ${!isSelected && hasEvents ? "bg-brand-green/10 border-brand-green/30 text-slate-900 hover:bg-brand-green/20 cursor-pointer" : ""}
                  ${!hasEvents ? "border-transparent text-slate-400" : ""}
                  ${isToday && !isSelected ? "ring-1 ring-brand-blue" : ""}`}
              >
                <span className={`font-semibold ${isSelected && hasEvents ? "text-white" : ""}`}>{date.getDate()}</span>
                {hasEvents && (
                  <span className={`mt-1 h-1.5 w-1.5 rounded-full ${isSelected ? "bg-white" : "bg-brand-green"}`} />
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 bg-brand-green/20 border border-brand-green/40 inline-block" /> Jour avec événement</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 ring-1 ring-brand-blue inline-block" /> Aujourd'hui</span>
        </div>
      </div>

      {/* Selected day events */}
      <div className="lg:col-span-5">
        <h4 className="text-sm font-bold uppercase tracking-wide text-brand-green mb-4">
          {selectedEvents.length > 0
            ? new Date(selected).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
            : "Sélectionnez un jour"}
        </h4>
        <div className="space-y-3" data-testid="cal-day-events">
          {selectedEvents.map((e) => {
            const d = fmt(e.start_date);
            return (
              <div key={e.id} className="bg-white border border-border rounded-sm p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-slate-900">{e.title}</h3>
                  {e.role && <span className="text-xs font-bold uppercase tracking-wide bg-brand-green/10 text-brand-green px-2 py-0.5">{e.role}</span>}
                </div>
                <p className="mt-1.5 text-sm text-slate-600">{e.description}</p>
                <div className="mt-3 space-y-1 text-sm text-slate-500">
                  <p className="flex items-center gap-1.5"><Calendar size={14} /> {d.time}</p>
                  <p className="flex items-center gap-1.5"><MapPin size={14} /> {e.location}</p>
                </div>
              </div>
            );
          })}
          {selectedEvents.length === 0 && (
            <p className="text-sm text-slate-400">Cliquez sur un jour marqué en vert pour voir les événements.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function EventList({ events }) {
  return (
    <div className="space-y-5">
      {events.map((e, i) => {
        const d = fmt(e.start_date);
        return (
          <div key={e.id} data-testid={`event-${i}`} className="group flex flex-col sm:flex-row gap-6 bg-white border border-border rounded-sm overflow-hidden hover:shadow-md transition-shadow">
            <div className="sm:w-32 bg-brand-blue text-white flex sm:flex-col items-center justify-center py-5 gap-2 sm:gap-0 shrink-0">
              <span className="text-4xl font-bold leading-none">{d.day}</span>
              <span className="text-sm uppercase tracking-wide">{d.month}</span>
            </div>
            {e.image_url && (
              <div className="sm:w-48 shrink-0 overflow-hidden">
                <img src={e.image_url} alt={e.title} className="w-full h-40 sm:h-full object-cover" />
              </div>
            )}
            <div className="p-5 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-xl font-bold text-slate-900">{e.title}</h3>
                {e.role && <span className="text-xs font-bold uppercase tracking-wide bg-brand-green/10 text-brand-green px-2.5 py-1">{e.role}</span>}
              </div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{e.description}</p>
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1.5"><Calendar size={15} /> {d.full} · {d.time}</span>
                <span className="flex items-center gap-1.5"><MapPin size={15} /> {e.location}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
